import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { getProductsCounts } from '../../../selectors/studio';
import ProductCategory from './ProductCategory';
import { ProductType } from '../types';
import Surface from '@wix/da-ds/pkg/Surface';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';

import s from './ProductCategories.scss';

interface Props {}

interface CategoryData {
  type: ProductType;
  productCount: number;
  originalsNewOrders?: number;
}

const ProductCategories: FC<Props> = () => {
  const counts = useSelector(getProductsCounts);
  const customExclusives = useFlag('custom_exclusives');
  const categories: CategoryData[] = [
    { type: ProductType.Exclusive, productCount: counts?.exclusive ?? 0 },
    { type: ProductType.PremiumDownload, productCount: counts?.pcp ?? 0 },
    {
      type: ProductType.PremiumGallery,
      productCount: counts?.premiumGallery ?? 0,
    },
    ...(customExclusives
      ? [
          {
            type: ProductType.Original,
            productCount: counts?.originals ?? 0,
            originalsNewOrders: counts?.originalsNewOrders,
          },
        ]
      : []),
    { type: ProductType.Commission, productCount: counts?.commission ?? 0 },
    { type: ProductType.Tier, productCount: counts?.tier ?? 0 },
  ];

  // move categories with 0 count to the bottom
  const sortedCategories = categories.sort((first, second) =>
    first.productCount === 0 && second.productCount !== 0
      ? 1
      : second.productCount === 0 && first.productCount !== 0
        ? -1
        : 0
  );

  return (
    <Surface type={ThemeSurface.SECONDARY} className={s['root']}>
      {sortedCategories.map(({ type, productCount, originalsNewOrders }) => (
        <ProductCategory
          key={type}
          type={type}
          productCount={productCount}
          originalsNewOrders={originalsNewOrders}
        />
      ))}
    </Surface>
  );
};
ProductCategories.displayName = 'ProductCategories';

export default ProductCategories;
